/**
 * Instanced vector overlay: anti-aliased line segments (with dash, glow, end fades) and
 * analytic shapes (small rings/arcs, glow dots, discs) and additive triangle fans (bearing
 * wedges, radar sweeps). Everything for a frame goes into three buffers → three draw calls.
 *
 * Positions are world mu plus an optional screen offset in CSS px (crisp glyphs that keep
 * their size at any zoom). Colours are linear RGB; the shader outputs premultiplied
 * colour where the glow part has alpha 0 (pure additive, phosphor-like).
 */
import { GLSL_COMMON, makeProgram, type GL, type Program } from './gl';
import type { RGB } from '../theme';
import type { FrameParams } from './frame';
import { expandQuads, QuadIndices, quadVao } from './quads';

const SEG_FLOATS = 20;
const SHAPE_FLOATS = 16;

const SHAPE_RING = 0;
const SHAPE_GLOW = 1;
const SHAPE_DISC = 2;

const SEG_VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
uniform float uDpr;
in vec2 aCorner;
in vec4 iAB;      // a.xy, b.xy (mu)
in vec4 iOff;     // offset a.xy, b.xy (css px)
in vec4 iStyle;   // width, s0, dashOn, dashOff (css px)
in vec4 iColor;
in vec4 iExtra;   // glow (css px), fadeA, fadeB, -
out vec2 vLocal;  // along, across (device px)
out float vLen;
flat out vec4 vStyle;
flat out vec4 vColor;
flat out vec4 vExtra;
void main() {
  vec2 pa = worldToPx(iAB.xy) + iOff.xy * uDpr;
  vec2 pb = worldToPx(iAB.zw) + iOff.zw * uDpr;
  vec2 d = pb - pa;
  float len = length(d);
  vec2 dir = len > 1e-4 ? d / len : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);
  float half_ = (iStyle.x * 0.5 + iExtra.x + 1.5) * uDpr;
  float along = aCorner.x * len + (aCorner.x * 2.0 - 1.0) * half_;
  float across = aCorner.y * half_;
  vec2 p = pa + dir * along + nrm * across;
  vLocal = vec2(along, across);
  vLen = len;
  vStyle = iStyle * uDpr;
  vColor = iColor;
  vExtra = vec4(iExtra.x * uDpr, iExtra.yzw);
  gl_Position = pxToClip(p);
}`;

const SEG_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vLocal;
in float vLen;
flat in vec4 vStyle;
flat in vec4 vColor;
flat in vec4 vExtra;
out vec4 outColor;
void main() {
  float t = clamp(vLocal.x, 0.0, vLen);
  float d = length(vec2(vLocal.x - t, vLocal.y));
  float hw = vStyle.x * 0.5;
  float core = 1.0 - smoothstep(hw - 0.6, hw + 0.6, d);
  if (vStyle.z > 0.0) {
    float s = vStyle.y + t;
    if (mod(s, vStyle.z + vStyle.w) > vStyle.z) core = 0.0;
  }
  float fade = mix(vExtra.y, vExtra.z, vLen > 0.0 ? t / vLen : 0.0);
  float a = vColor.a * fade;
  float glow = vExtra.x > 0.0 ? exp(-max(d - hw, 0.0) / vExtra.x) * 0.45 * a : 0.0;
  if (vStyle.z > 0.0 && core == 0.0) glow *= 0.35;
  vec3 rgb = vColor.rgb;
  outColor = vec4(rgb * (core * a + glow * (1.0 - core)), core * a);
}`;

const SHAPE_VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
uniform float uDpr;
in vec2 aCorner;
in vec4 iGeom;    // cx, cy, radius, kind
in vec4 iColor;
in vec4 iP0;      // width, dashOn, dashOff, a0 (discs / squares: -, offset x, offset y (css px), -)
in vec4 iP1;      // a1, r0, flags (1 = radius in css px), glow
out vec2 vRel;
flat out float vR;
flat out float vR0;
flat out float vExt;
flat out vec4 vGeom;
flat out vec4 vColor;
flat out vec4 vP0;
flat out vec4 vP1;
void main() {
  bool rpx = mod(iP1.z, 2.0) >= 1.0;
  float R = rpx ? iGeom.z * uDpr : iGeom.z * uCam.z;
  float R0 = rpx ? iP1.y * uDpr : iP1.y * uCam.z;
  float ext = R + (iP0.x + 3.0 * iP1.w + 2.0) * uDpr;
  vExt = ext;
  vec2 c = worldToPx(iGeom.xy) + (iGeom.w > 1.5 ? iP0.yz * uDpr : vec2(0.0));
  vec2 corner = aCorner.x < 0.5 ? vec2(-1.0, aCorner.y) : vec2(1.0, aCorner.y);
  vec2 p = c + corner * ext;
  vRel = p - c;
  vR = R;
  vR0 = R0;
  vGeom = iGeom;
  vColor = iColor;
  vP0 = vec4(iP0.x * uDpr, iP0.y * uDpr, iP0.z * uDpr, iP0.w);
  vP1 = vec4(iP1.xyz, iP1.w * uDpr);
  gl_Position = pxToClip(p);
}`;

const SHAPE_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vRel;
flat in float vR;
flat in float vR0;
flat in float vExt;
flat in vec4 vGeom;
flat in vec4 vColor;
flat in vec4 vP0;
flat in vec4 vP1;
out vec4 outColor;
const float TAU = 6.2831853;
float angDelta(float a, float from) { return mod(a - from, TAU); }
void main() {
  int kind = int(vGeom.w + 0.5);
  float d = length(vRel);
  float ang = atan(vRel.y, vRel.x);
  float a0 = vP0.w, a1 = vP1.x;
  float span = a1 - a0 >= TAU - 1e-3 ? TAU : mod(a1 - a0, TAU);
  bool inArc = span >= TAU - 1e-3 || angDelta(ang, a0) <= span;
  float A = vColor.a;
  float core = 0.0, glow = 0.0;
  if (kind == 0) {               // ring / arc
    float hw = vP0.x * 0.5;
    float dd = abs(d - vR);
    core = 1.0 - smoothstep(hw - 0.6, hw + 0.6, dd);
    if (vP0.y > 0.0) {
      float s = angDelta(ang, a0) * vR;
      if (mod(s, vP0.y + vP0.z) > vP0.y) core = 0.0;
    }
    if (vP1.w > 0.0) glow = exp(-max(dd - hw, 0.0) / vP1.w) * 0.4;
    if (!inArc) { core = 0.0; glow = 0.0; }
  } else if (kind == 1) {        // glow dot (additive), reaching exactly 0 at its radius
    float x = d / max(vR, 1e-3);
    glow = exp(-x * x * 3.0) * (1.0 - smoothstep(0.6, 1.0, x));
  } else {                       // solid disc (flag 2: square)
    if (mod(floor(vP1.z / 2.0), 2.0) >= 1.0) d = max(abs(vRel.x), abs(vRel.y));
    core = 1.0 - smoothstep(vR - 0.7, vR + 0.7, d);
    if (vP1.w > 0.0) glow = exp(-max(d - vR, 0.0) / vP1.w) * 0.5;
  }
  // every glow reaches 0 inside the quad's inscribed circle: no visible square edges
  glow *= 1.0 - smoothstep(vExt - 2.0 * vP1.w - 2.0, vExt, d);
  outColor = vec4(vColor.rgb * (core * A + glow * A * (1.0 - core)), core * A);
}`;

const TRI_VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
in vec2 aPos;
in vec4 aColor;
out vec4 vColor;
void main() {
  vColor = aColor;
  gl_Position = pxToClip(worldToPx(aPos));
}`;

const TRI_FS = /* glsl */ `#version 300 es
precision highp float;
in vec4 vColor;
out vec4 outColor;
void main() {
  // additive: premultiplied colour, alpha 0
  outColor = vec4(vColor.rgb * vColor.a, 0.0);
}`;

const TRI_FLOATS = 6;

export interface SegStyle {
  width?: number;
  dash?: [number, number];
  /** dash phase (css px) */
  phase?: number;
  glow?: number;
  fadeA?: number;
  fadeB?: number;
}

export interface RingStyle {
  width?: number;
  dash?: [number, number];
  /** arc start / end, radians (map space) */
  a0?: number;
  a1?: number;
  glow?: number;
  /** radius given in css px instead of mu */
  px?: boolean;
}

class Buf {
  data: Float32Array;
  n = 0;
  constructor(
    readonly stride: number,
    cap: number,
  ) {
    this.data = new Float32Array(stride * cap);
  }
  push(): number {
    if ((this.n + 1) * this.stride > this.data.length) {
      const next = new Float32Array(this.data.length * 2);
      next.set(this.data);
      this.data = next;
    }
    return this.n++ * this.stride;
  }
}

export class VectorBatch {
  private segs = new Buf(SEG_FLOATS, 4096);
  private shapes = new Buf(SHAPE_FLOATS, 512);
  private tris = new Buf(TRI_FLOATS, 2048);
  /** css px per mu this frame (ring tessellation, dash phase) */
  private zoom = 1;
  /** visible world rect with margin, for culling */
  private view: [number, number, number, number] = [-1e9, -1e9, 1e9, 1e9];
  private triProg: Program;
  private triVao: WebGLVertexArrayObject;
  private triBuf: WebGLBuffer;
  private segProg: Program;
  private shapeProg: Program;
  private segVao: WebGLVertexArrayObject;
  private shapeVao: WebGLVertexArrayObject;
  private segBuf: WebGLBuffer;
  private shapeBuf: WebGLBuffer;

  private idx: QuadIndices;
  private expanded: Float32Array = new Float32Array(0);

  constructor(private gl: GL) {
    this.segProg = makeProgram(gl, 'seg', SEG_VS, SEG_FS, ['uView', 'uCam', 'uDpr']);
    this.shapeProg = makeProgram(gl, 'shape', SHAPE_VS, SHAPE_FS, ['uView', 'uCam', 'uDpr']);
    this.idx = new QuadIndices(gl);
    this.segBuf = gl.createBuffer()!;
    this.shapeBuf = gl.createBuffer()!;
    this.segVao = quadVao(gl, this.segProg.prog, this.segBuf, SEG_FLOATS, ['iAB', 'iOff', 'iStyle', 'iColor', 'iExtra'], this.idx);
    this.shapeVao = quadVao(gl, this.shapeProg.prog, this.shapeBuf, SHAPE_FLOATS, ['iGeom', 'iColor', 'iP0', 'iP1'], this.idx);
    this.triProg = makeProgram(gl, 'tris', TRI_VS, TRI_FS, ['uView', 'uCam']);
    this.triBuf = gl.createBuffer()!;
    this.triVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.triVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.triBuf);
    const lp = gl.getAttribLocation(this.triProg.prog, 'aPos');
    const lcol = gl.getAttribLocation(this.triProg.prog, 'aColor');
    gl.enableVertexAttribArray(lp);
    gl.vertexAttribPointer(lp, 2, gl.FLOAT, false, TRI_FLOATS * 4, 0);
    gl.enableVertexAttribArray(lcol);
    gl.vertexAttribPointer(lcol, 4, gl.FLOAT, false, TRI_FLOATS * 4, 8);
    gl.bindVertexArray(null);
  }

  /** Start a frame: zoom in css px per mu and the visible world rect (with margin). */
  begin(zoomCss: number, view: [number, number, number, number]): void {
    this.segs.n = 0;
    this.shapes.n = 0;
    this.tris.n = 0;
    this.zoom = zoomCss;
    this.view = view;
  }

  private offscreen(x: number, y: number, r: number): boolean {
    const v = this.view;
    return x + r < v[0] || x - r > v[2] || y + r < v[1] || y - r > v[3];
  }

  private vert(x: number, y: number, col: RGB, a: number): void {
    const o = this.tris.push();
    const d = this.tris.data;
    d[o] = x;
    d[o + 1] = y;
    d[o + 2] = col[0];
    d[o + 3] = col[1];
    d[o + 4] = col[2];
    d[o + 5] = a;
  }

  /** World-space line a→b, with optional per-end screen offsets (css px). */
  line(ax: number, ay: number, bx: number, by: number, col: RGB, alpha: number, s: SegStyle = {}, aox = 0, aoy = 0, box = 0, boy = 0): void {
    const v = this.view;
    if (Math.max(ax, bx) < v[0] || Math.min(ax, bx) > v[2] || Math.max(ay, by) < v[1] || Math.min(ay, by) > v[3]) return;
    const o = this.segs.push();
    const d = this.segs.data;
    d[o] = ax;
    d[o + 1] = ay;
    d[o + 2] = bx;
    d[o + 3] = by;
    d[o + 4] = aox;
    d[o + 5] = aoy;
    d[o + 6] = box;
    d[o + 7] = boy;
    d[o + 8] = s.width ?? 1.2;
    d[o + 9] = s.phase ?? 0;
    d[o + 10] = s.dash ? s.dash[0] : 0;
    d[o + 11] = s.dash ? s.dash[1] : 0;
    d[o + 12] = col[0];
    d[o + 13] = col[1];
    d[o + 14] = col[2];
    d[o + 15] = alpha;
    d[o + 16] = s.glow ?? 0;
    d[o + 17] = s.fadeA ?? 1;
    d[o + 18] = s.fadeB ?? 1;
    d[o + 19] = 0;
  }

  /** Screen-space glyph stroke anchored at world (x, y): offsets in css px. */
  glyph(x: number, y: number, x0: number, y0: number, x1: number, y1: number, col: RGB, alpha: number, s: SegStyle = {}): void {
    this.line(x, y, x, y, col, alpha, s, x0, y0, x1, y1);
  }

  private shape(
    kind: number,
    x: number,
    y: number,
    r: number,
    col: RGB,
    alpha: number,
    p0: [number, number, number, number],
    p1: [number, number, number, number],
  ): void {
    const o = this.shapes.push();
    const d = this.shapes.data;
    d[o] = x;
    d[o + 1] = y;
    d[o + 2] = r;
    d[o + 3] = kind;
    d[o + 4] = col[0];
    d[o + 5] = col[1];
    d[o + 6] = col[2];
    d[o + 7] = alpha;
    d.set(p0, o + 8);
    d.set(p1, o + 12);
  }

  ring(x: number, y: number, r: number, col: RGB, alpha: number, s: RingStyle = {}): void {
    const full = s.a0 === undefined;
    if (s.px) {
      this.shape(
        SHAPE_RING,
        x,
        y,
        r,
        col,
        alpha,
        [s.width ?? 1.2, s.dash?.[0] ?? 0, s.dash?.[1] ?? 0, full ? 0 : s.a0!],
        [full ? 7 : s.a1!, 0, 1, s.glow ?? 0],
      );
      return;
    }
    // world-size rings are tessellated: cost follows the perimeter, not the disc area
    if (this.offscreen(x, y, r)) return;
    const a0 = full ? 0 : s.a0!;
    const a1 = full ? Math.PI * 2 : s.a1!;
    const rPx = r * this.zoom;
    const n = Math.max(12, Math.min(220, Math.ceil(((a1 - a0) * rPx) / 7)));
    const seg: SegStyle = { width: s.width ?? 1.2, glow: s.glow ?? 0, ...(s.dash ? { dash: s.dash } : {}) };
    let px = x + Math.cos(a0) * r;
    let py = y + Math.sin(a0) * r;
    for (let i = 1; i <= n; i++) {
      const t = a0 + ((a1 - a0) * i) / n;
      const nx = x + Math.cos(t) * r;
      const ny = y + Math.sin(t) * r;
      seg.phase = (a0 + ((a1 - a0) * (i - 1)) / n) * rPx;
      this.line(px, py, nx, ny, col, alpha, seg);
      px = nx;
      py = ny;
    }
  }

  /** Soft additive glow; radius in css px. */
  glowDot(x: number, y: number, rPx: number, col: RGB, alpha: number): void {
    this.shape(SHAPE_GLOW, x, y, rPx, col, alpha, [0, 0, 0, 0], [7, 0, 1, 0]);
  }

  /** Filled disc; radius in css px unless `mu`. */
  disc(x: number, y: number, r: number, col: RGB, alpha: number, glowPx = 0, mu = false): void {
    this.shape(SHAPE_DISC, x, y, r, col, alpha, [0, 0, 0, 0], [7, 0, mu ? 0 : 1, glowPx]);
  }

  /** Filled square, half-size in css px; (dx, dy): its centre's offset from (x, y), css px. */
  square(x: number, y: number, halfPx: number, col: RGB, alpha: number, glowPx = 0, dx = 0, dy = 0): void {
    this.shape(SHAPE_DISC, x, y, halfPx, col, alpha, [0, dx, dy, 0], [7, 0, 3, glowPx]);
  }

  /** Bearing wedge between radii r0..r1 (mu) and angles a0..a1, fading outward (additive). */
  wedge(x: number, y: number, r0: number, r1: number, a0: number, a1: number, col: RGB, alpha: number): void {
    if (this.offscreen(x, y, r1)) return;
    const n = Math.max(4, Math.ceil((a1 - a0) * 12));
    // two radial bands give a roughly quadratic falloff
    const rm = r0 + (r1 - r0) * 0.35;
    const bands: [number, number, number, number][] = [
      [r0, rm, alpha, alpha * 0.35],
      [rm, r1, alpha * 0.35, 0],
    ];
    for (let i = 0; i < n; i++) {
      const t0 = a0 + ((a1 - a0) * i) / n;
      const t1 = a0 + ((a1 - a0) * (i + 1)) / n;
      // soften the two side edges
      const e0 = i === 0 ? 0.25 : 1;
      const e1 = i === n - 1 ? 0.25 : 1;
      const c0 = Math.cos(t0);
      const s0 = Math.sin(t0);
      const c1 = Math.cos(t1);
      const s1 = Math.sin(t1);
      for (const [ra, rb, aa, ab] of bands) {
        this.vert(x + c0 * ra, y + s0 * ra, col, aa * e0);
        this.vert(x + c0 * rb, y + s0 * rb, col, ab * e0);
        this.vert(x + c1 * rb, y + s1 * rb, col, ab * e1);
        this.vert(x + c0 * ra, y + s0 * ra, col, aa * e0);
        this.vert(x + c1 * rb, y + s1 * rb, col, ab * e1);
        this.vert(x + c1 * ra, y + s1 * ra, col, aa * e1);
      }
    }
  }

  /**
   * Radar sweep: brightest at angle `a`, trailing `span` radians (radius in mu). The leading
   * edge ramps in over a few degrees too, so neither side is a hard straight line (in a still
   * frame a hard edge reads as a rendering seam).
   */
  sweep(x: number, y: number, r: number, a: number, span: number, col: RGB, alpha: number): void {
    if (this.offscreen(x, y, r)) return;
    const lead = span * 0.12;
    this.vert(x, y, col, alpha * 0.25);
    this.vert(x + Math.cos(a + lead) * r, y + Math.sin(a + lead) * r, col, 0);
    this.vert(x + Math.cos(a) * r, y + Math.sin(a) * r, col, alpha);
    const n = 10;
    for (let i = 0; i < n; i++) {
      const t0 = a - (span * i) / n;
      const t1 = a - (span * (i + 1)) / n;
      const k0 = alpha * Math.pow(1 - i / n, 4);
      const k1 = alpha * Math.pow(1 - (i + 1) / n, 4);
      this.vert(x, y, col, (k0 + k1) * 0.25);
      this.vert(x + Math.cos(t0) * r, y + Math.sin(t0) * r, col, k0);
      this.vert(x + Math.cos(t1) * r, y + Math.sin(t1) * r, col, k1);
    }
  }

  draw(f: FrameParams): void {
    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    if (this.tris.n) {
      const p = this.triProg;
      gl.useProgram(p.prog);
      gl.uniform2f(p.u.uView!, f.viewW, f.viewH);
      gl.uniform3f(p.u.uCam!, f.camX, f.camY, f.zoomPx);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.triBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.tris.data.subarray(0, this.tris.n * TRI_FLOATS), gl.STREAM_DRAW);
      gl.bindVertexArray(this.triVao);
      gl.drawArrays(gl.TRIANGLES, 0, this.tris.n);
      gl.bindVertexArray(null);
    }
    if (this.shapes.n) this.drawBuf(this.shapeProg, this.shapeVao, this.shapeBuf, this.shapes, f);
    if (this.segs.n) this.drawBuf(this.segProg, this.segVao, this.segBuf, this.segs, f);
    gl.disable(gl.BLEND);
  }

  private drawBuf(p: Program, vao: WebGLVertexArrayObject, buf: WebGLBuffer, b: Buf, f: FrameParams): void {
    const gl = this.gl;
    gl.useProgram(p.prog);
    gl.uniform2f(p.u.uView!, f.viewW, f.viewH);
    gl.uniform3f(p.u.uCam!, f.camX, f.camY, f.zoomPx);
    gl.uniform1f(p.u.uDpr!, f.dpr);
    this.expanded = expandQuads(b.data, b.stride, b.n, this.expanded);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, this.expanded.subarray(0, b.n * 4 * (b.stride + 2)), gl.STREAM_DRAW);
    gl.bindVertexArray(vao);
    this.idx.ensure(b.n);
    gl.drawElements(gl.TRIANGLES, b.n * 6, gl.UNSIGNED_INT, 0);
    gl.bindVertexArray(null);
  }
}
